import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ScriptureAnchor } from '@/types/prayer';
import type { BibleBookmark } from '@/types/bookmark';
import { useVoiceCapture } from '@/hooks/useVoiceCapture';
import { useBookmarkStore } from '@/lib/bookmark-store';
import { notesCoveringVerse } from '@/lib/bookmark-utils';
import {
  formatVerseRanges,
  parseScriptureAnchor,
  scriptureAnchorsEqual,
} from '@/lib/scripture-anchor';
import { formatFullDateTime } from '@/lib/prayer-utils';
import { JOURNAL_PAGE_SIZE } from '@/lib/journal-aggregate';
import { useSaveCue } from '@/hooks/useSaveCue';
import { enhancedVoiceAllowed, isCoreReadOnly } from '@/lib/core-cutover';
import { BookmarkStaleEditError } from '@/lib/bookmark-errors';
import {
  collectInlineAudioCommitClaims,
  saveRefusedForUnreadableRecording,
  discardAwaitingInlineSaveAudio,
  finalizeCommittedAudio,
  prepareBookmarkRecordingWords,
  recordingWordsStatus,
  subscribeAudioQueueActivity,
} from '@/lib/audio-queue';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import { syncContentLifecycle } from '@/lib/content-lifecycle/sync';
import { mergeRecoveredVoiceText } from '@/lib/audio-recovery';
import {
  audioQueueMetaCarriesBytes,
  audioQueueMetaDeletionAllowed,
  requiresEnhancedVoiceProcessing,
  useAudioQueueStore,
} from '@/lib/audio-queue-store';
import MicButton from '../MicButton';
import SheetShell from '../SheetShell';
import JournalPagination from '../JournalPagination/JournalPagination';
import BookmarkUnconfirmedSaveNotice from './BookmarkUnconfirmedSaveNotice';
import styles from './BibleBookmarkSheet.module.css';

type BibleBookmarkSheetProps = (
  // COMPOSE: selection -> create a passage note. onSaved is the
  // close-on-save callback (-> BibleChapter.handleBookmarkSaved).
  | {
      mode: 'compose';
      anchor: ScriptureAnchor;
      voiceTarget: string;
      onClose: () => void;
      onSaved: () => void;
    }
  // THREAD: verse-tap -> all notes saved on that verse over time. NO onSaved
  // (thread-redesign §5, Revision 5 LOW): "Add a note" does NOT close the
  // sheet; it closes only via onClose (backdrop / Escape / last-note-delete).
  | {
      mode: 'thread';
      anchor: ScriptureAnchor;
      voiceTarget: string;
      onClose: () => void;
      /** The verse whose thread is open when an honest frozen fallback must
       *  show a wider passage anchor. */
      threadVerse?: number;
      /** False only when the frozen passage cannot be split into an exact
       *  single-verse snapshot without guessing. Existing entries remain
       *  editable; creating a wrongly anchored entry does not. */
      allowCreate?: boolean;
    }
) & {
  /** Coordinator z for the `bookmark-compose` / `bookmark-thread` NavStack
   *  layer (AB, rule #78). */
  zIndex?: number;
  /** 'sheet' (default) wraps in SheetShell with the internal header and the
   *  Escape/backdrop/requestClose close paths. 'page' (Scripture Journal
   *  reflection route, Phase 4 §5) renders the composer + thread body bare
   *  (no SheetShell, no internal header, NO Escape/backdrop close) so a route
   *  can present it as a warm page; the route owns the back affordance, the
   *  verse header, and the Read-in-full-context CTA. In 'page' mode onClose
   *  fires ONLY from the last-note delete (§5.1); the NavLayer/AB ref is never
   *  wired by the route, so requestClose is inert. */
  presentation?: 'sheet' | 'page';
};

/**
 * Imperative handle (AB, rule #78 §4a): BibleChapter's bookmark layer close
 * (browser/swipe back) calls requestClose() so back routes through the sheet's
 * own stateful close (saved->finalize / idle->abandon /
 * saving->defer-then-complete-on-settle), making back ≡ backdrop ≡ Escape.
 */
export interface BibleBookmarkSheetHandle {
  requestClose: () => void;
}

/**
 * Bookmark-and-note sheet (Bible reader, thread-redesign §5). One component,
 * two modes:
 *
 * - COMPOSE = the create flow: a slide-up sheet over the still-visible
 *   chapter that creates a BibleBookmark carrying the frozen ScriptureAnchor
 *   + an OPTIONAL note, then closes (brief "Saved" flash, D10).
 * - THREAD = the verse-tap flow: the add composer at the TOP (D4), then all
 *   notes covering the tapped verse, newest-first, each with an IMMUTABLE
 *   created stamp (formatFullDateTime, §6) + a passage label when the note
 *   covers more than the tapped verse, plus per-note text-only edit (D9) and
 *   delete. Adding a note keeps the sheet open (the new note appears in the
 *   list, D10); deleting the last note covering the verse closes it.
 *
 * Both modes share the composer + voice grammar (SheetShell, useVoiceCapture
 * target, MicButton, useSaveCue), and crucially NO claim-into-prayer branch.
 *
 * Voice safety (feature plan §8.2): this is the ONLY surface that records
 * with `inlineSaveSource: 'bookmark'`. A failed bookmark recording parks a
 * bookmark-source orphan that PrayPage's claim path ignores (positive
 * allowlist). The row remains owner-bound until an exact successful note
 * commit consumes it or the user explicitly discards saved Bible audio.
 * Recovery on a transcribe-fail is "simple retry" (§8.1): the note is
 * optional, so the verse-only bookmark still saves and a calm notice offers
 * another try, typing, and the explicit storage-pressure escape hatch.
 */
const BibleBookmarkSheet = forwardRef<
  BibleBookmarkSheetHandle,
  BibleBookmarkSheetProps
>(function BibleBookmarkSheet(props, ref) {
  const { mode, anchor, voiceTarget, onClose, zIndex } = props;
  const presentation = props.presentation ?? 'sheet';
  const allowCreate = props.mode === 'thread' ? (props.allowCreate ?? true) : true;
  // COMPOSE-only close-on-save; undefined in THREAD mode (no onSaved prop).
  const onSaved = props.mode === 'compose' ? props.onSaved : undefined;

  const { t, i18n } = useTranslation();
  const readOnly = isCoreReadOnly();
  const voice = useVoiceCapture();
  const registerVoiceTarget = voice.registerTarget;
  const cancelVoice = voice.cancel;
  const addBookmark = useBookmarkStore((s) => s.addBookmark);
  const updateBookmark = useBookmarkStore((s) => s.updateBookmark);
  const removeBookmark = useBookmarkStore((s) => s.removeBookmark);
  const allBookmarks = useBookmarkStore((s) => s.bookmarks);

  // Thread verse = the tapped verse (single-verse anchor). For a compose
  // anchor (a passage selection) this is the first selected verse and the
  // note list is not rendered, so the value is harmless there.
  const threadVerse =
    props.mode === 'thread' ? (props.threadVerse ?? anchor.verses[0] ?? -1) : -1;
  const notes = useMemo(
    () =>
      notesCoveringVerse(allBookmarks, anchor.bookSlug, anchor.chapter, threadVerse),
    [allBookmarks, anchor.bookSlug, anchor.chapter, threadVerse],
  );

  // Committed text, ref-mirrored so the save callback reads fresh text after
  // `await voice.stop()` even before React re-renders (mirrors usePrayerUpdate).
  const [text, setTextState] = useState('');
  const textRef = useRef('');
  const setText = useCallback(
    (value: string | ((prev: string) => string)) => {
      const next = typeof value === 'function' ? value(textRef.current) : value;
      textRef.current = next;
      setTextState(next);
    },
    [],
  );

  const [saved, setSaved] = useState(false);
  // Simple-retry notice (§8.1): true when a recording settled with no text.
  const [voiceFailed, setVoiceFailed] = useState(false);
  const [discardingVoiceRecovery, setDiscardingVoiceRecovery] = useState(false);
  const [adoptedVoiceRecoveryId, setAdoptedVoiceRecoveryId] = useState<string | null>(null);
  // Mic repair F3 (2026-10-20): the recordings whose words THIS sheet received
  // (component state, so the list below re-renders). Their words are already in
  // the box, so the list never offers them again as words "from an interrupted
  // recording". The Save still claims them (committedInlineAudioIdsRef).
  const [receivedVoiceIds, setReceivedVoiceIds] = useState<ReadonlySet<string>>(() => new Set());
  // Mic repair F4: the last "Discard saved audio" did not happen (the recording
  // is still kept); the notice says so instead of failing silently.
  const [discardVoiceFailed, setDiscardVoiceFailed] = useState(false);
  const audioQueueItems = useAudioQueueStore((state) => state.items);
  const bookmarkRecoveryItems = useMemo(
    () => audioQueueItems
      .filter((item) => {
        const recoveryAnchor = parseScriptureAnchor(item.scriptureAnchor);
        return (
          !receivedVoiceIds.has(item.id) &&
          item.awaitingInlineSave &&
          item.localDisposition === 'unresolved' &&
          (item.destinationState === undefined || item.destinationState === 'unresolved') &&
          item.inlineSaveSource === 'bookmark' &&
          recoveryAnchor !== null &&
          scriptureAnchorsEqual(recoveryAnchor, anchor)
        );
      })
      .sort((a, b) => b.capturedAt.localeCompare(a.capturedAt)),
    [audioQueueItems, anchor, receivedVoiceIds],
  );
  const bookmarkRecovery = bookmarkRecoveryItems[0];
  const bookmarkRecoveryText =
    typeof bookmarkRecovery?.transcribedText === 'string'
      ? bookmarkRecovery.transcribedText.trim()
      : '';
  const bookmarkRecoveryCount = bookmarkRecoveryItems.length;
  const bookmarkAudioRecoveryCount = bookmarkRecoveryItems.filter(
    (item) => requiresEnhancedVoiceProcessing(item),
  ).length;
  const bookmarkRecoveryParked = Boolean(
    bookmarkRecovery &&
    !audioQueueMetaDeletionAllowed(bookmarkRecovery, enhancedVoiceAllowed()),
  );
  // Part R (amendment A2, A3): an explicit "Get the words" for a recovery row
  // that holds audio but no words. The status comes from the row and this
  // page's own in-flight work, which notifies when a run starts or ends.
  const [, setRecoveryActivityTick] = useState(0);
  useEffect(
    () => subscribeAudioQueueActivity(() => setRecoveryActivityTick((n) => n + 1)),
    [],
  );
  const recoveryOwnerId = getAuthUserId();
  const bookmarkRecoveryStatus =
    bookmarkRecovery && typeof recoveryOwnerId === 'string'
      ? recordingWordsStatus(bookmarkRecovery, recoveryOwnerId, getAuthEpoch())
      : null;
  const canPrepareBookmarkWords = Boolean(
    bookmarkRecovery &&
    !bookmarkRecoveryText &&
    !bookmarkRecoveryParked &&
    enhancedVoiceAllowed() &&
    audioQueueMetaCarriesBytes(bookmarkRecovery) &&
    bookmarkRecoveryStatus !== 'processing' &&
    adoptedVoiceRecoveryId !== bookmarkRecovery.id,
  );
  const handlePrepareBookmarkWords = () => {
    if (!bookmarkRecovery) return;
    void prepareBookmarkRecordingWords(bookmarkRecovery.id, { explicit: true });
  };
  // Per-note inline edit (text-only, D9) + delete confirm (thread mode).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editingBaseUpdatedAt, setEditingBaseUpdatedAt] = useState<string | null>(null);
  const [staleEdit, setStaleEdit] = useState<{
    id: string;
    latestText: string;
    latestUpdatedAt: string;
  } | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(
    null,
  );
  const [threadPage, setThreadPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const [editingSaveId, setEditingSaveId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // `saveReopen` (LC7, review D-B): the Save was refused because a recording
  // it claims cannot be read in this page; trying again here cannot succeed.
  const [mutationError, setMutationError] = useState<
    'save' | 'saveReopen' | 'edit' | 'delete' | null
  >(null);

  const threadPageCount = Math.max(
    1,
    Math.ceil(notes.length / JOURNAL_PAGE_SIZE),
  );
  const boundedThreadPage = Math.min(threadPage, threadPageCount - 1);
  const threadStart = boundedThreadPage * JOURNAL_PAGE_SIZE;
  const shownNotes = notes.slice(
    threadStart,
    threadStart + JOURNAL_PAGE_SIZE,
  );

  // True once THIS target actually started recording. Latched via effect on
  // isListening (NOT the mic toggle) so a dismissed first-tap disclosure
  // leaves it false. Stays true after a recording ends so a transcribe-fail
  // before Save still scopes the orphan cleanup to this sheet.
  const recordedHere = useRef(false);
  const committedInlineAudioIdsRef = useRef(new Set<string>());
  // Whether onFinal delivered any text during the current session. Reset at
  // each recording start; drives the voiceFailed notice.
  const gotFinalRef = useRef(false);
  // Tracks the capturing -> settled transition for the voiceFailed check.
  const wasCapturingRef = useRef(false);
  // Synchronous in-flight guard against rapid double-tap Save.
  const savingRef = useRef(false);
  // AB (rule #78 §4a): set when a browser/swipe back arrives mid-save; the
  // dismiss is completed in handleSave's finally once the save settles.
  const pendingBackRef = useRef(false);
  // Tracks mount so an in-flight save resolving after unmount never setStates.
  const mountedRef = useRef(true);

  const isThisTarget = voice.target === voiceTarget;
  const isListening = voice.isRecording && isThisTarget;
  const isTranscribing = voice.isTranscribing && isThisTarget;
  const interim = isThisTarget ? voice.interim : '';

  const displayValue = interim
    ? text
      ? text + ' ' + interim
      : interim
    : text;

  const showSaveCue = useSaveCue(isListening || isTranscribing, displayValue);

  // Register the voice target with bookmark source. Cleanup cancels active
  // capture before unregistering. Already-durable audio remains in recovery
  // storage until a saved note proves its transcript reached the destination.
  useEffect(() => {
    if (readOnly) return;
    const unregister = registerVoiceTarget(voiceTarget, {
      onFinal: (finalText, queueItemId) => {
        if (queueItemId) {
          committedInlineAudioIdsRef.current.add(queueItemId);
          setReceivedVoiceIds((current) => (current.has(queueItemId) ? current : new Set(current).add(queueItemId)));
        }
        gotFinalRef.current = true;
        setText((prev) => (prev ? prev + ' ' + finalText : finalText));
      },
      getRecoveryText: () => textRef.current,
    });
    return () => {
      void cancelVoice();
      unregister();
    };
  }, [
    registerVoiceTarget,
    cancelVoice,
    voiceTarget,
    setText,
    readOnly,
  ]);

  // Latch recordedHere when this target genuinely starts recording.
  useEffect(() => {
    if (isListening) recordedHere.current = true;
  }, [isListening]);

  // Detect the capturing -> settled transition: if a recording produced no
  // text, surface the simple-retry notice. set-state-in-effect is the right
  // tool here (reacting to async voice state, not derivable synchronously);
  // documented deviation, same convention as the reader's load effects.
  useEffect(() => {
    const capturing = isListening || isTranscribing;
    if (capturing) {
      wasCapturingRef.current = true;
      return;
    }
    if (wasCapturingRef.current) {
      wasCapturingRef.current = false;
      if (recordedHere.current && !gotFinalRef.current) {
        setVoiceFailed(true);
      }
    }
  }, [isListening, isTranscribing]);

  // Flip mountedRef on unmount so a late-resolving save can't setState here.
  // Set true on mount: Vite + React StrictMode remounts with the SAME ref
  // after cleanup ran, so a cleanup-only effect left this false forever
  // and compose Save persisted the note but never flashed Saved / toast.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const toggleMic = useCallback(() => {
    if (readOnly) return;
    if (isListening) {
      if (interim) {
        // Browser STT can leave un-finalized interim when the user stops
        // mid-utterance; merging it into the note IS a yield, so mark the
        // session as having produced text or the settled-state effect would
        // wrongly show the voiceFailed notice.
        gotFinalRef.current = true;
        setText((prev) => (prev ? prev + ' ' + interim : interim));
      }
      void voice.stop().catch(() => {});
      return;
    }
    // Fresh recording: reset the no-text tracking and clear any prior notice.
    gotFinalRef.current = false;
    setVoiceFailed(false);
    setDiscardVoiceFailed(false);
    voice.start(voiceTarget, {
      inlineSaveSource: 'bookmark',
      scriptureAnchor: anchor,
    });
  }, [isListening, interim, voice, voiceTarget, setText, readOnly, anchor]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (readOnly) return;
      if (isListening || interim) {
        void voice.stop().catch(() => {});
      }
      setVoiceFailed(false);
      setText(e.target.value);
    },
    [isListening, interim, voice, setText, readOnly],
  );

  const handleDiscardVoiceRecovery = useCallback(async () => {
    if (
      discardingVoiceRecovery ||
      !bookmarkRecovery ||
      adoptedVoiceRecoveryId === bookmarkRecovery.id ||
      !audioQueueMetaDeletionAllowed(bookmarkRecovery, enhancedVoiceAllowed())
    ) return;
    setDiscardingVoiceRecovery(true);
    setDiscardVoiceFailed(false);
    const offeredId = bookmarkRecovery.id;
    try {
      let discarded = false;
      try {
        discarded = await discardAwaitingInlineSaveAudio(offeredId, 'bookmark');
      } catch {
        discarded = false;
      }
      if (!discarded) {
        // Mic repair F4: still offered once the discard settled means it did
        // not happen (no answer, a refusal, or a failed write): the recording is
        // kept and the notice says so. A recording that left the offer (taken
        // by the registry's answer, or by another page) is not a failure.
        const stillOffered = useAudioQueueStore.getState().items.some(
          (item) => item.id === offeredId && item.awaitingInlineSave && item.localDisposition === 'unresolved',
        );
        if (stillOffered && mountedRef.current) setDiscardVoiceFailed(true);
        return;
      }
      committedInlineAudioIdsRef.current.delete(bookmarkRecovery.id);
      setAdoptedVoiceRecoveryId((current) =>
        current === bookmarkRecovery.id ? null : current,
      );
      setVoiceFailed(false);
    } finally {
      if (mountedRef.current) setDiscardingVoiceRecovery(false);
    }
  }, [discardingVoiceRecovery, bookmarkRecovery, adoptedVoiceRecoveryId]);

  const handleUseVoiceRecovery = useCallback(() => {
    if (!bookmarkRecovery || !bookmarkRecoveryText || bookmarkRecoveryParked) return;
    committedInlineAudioIdsRef.current.add(bookmarkRecovery.id);
    setText((current) => mergeRecoveredVoiceText(current, bookmarkRecoveryText));
    setAdoptedVoiceRecoveryId(bookmarkRecovery.id);
    setVoiceFailed(false);
  }, [bookmarkRecovery, bookmarkRecoveryText, bookmarkRecoveryParked, setText]);

  // Abandon: cancel-before-close (drops in-flight audio promptly); the
  // unmount cleanup's second cancel is then a harmless no-op.
  const handleClose = useCallback(() => {
    if (savingRef.current) return;
    void cancelVoice();
    onClose();
  }, [cancelVoice, onClose]);

  const finalize = useCallback(() => {
    onSaved?.();
  }, [onSaved]);

  const handleBackdrop = useCallback(() => {
    if (saved) finalize();
    else handleClose();
  }, [saved, finalize, handleClose]);

  // AB (rule #78 §4a): browser/swipe back close path. Mirrors handleBackdrop
  // for the saved/idle states, but DEFERS the saving state (sets pendingBackRef)
  // because the history entry is already consumed — completed in handleSave's
  // finally. Exposed imperatively for BibleChapter's bookmark layer close.
  const requestClose = useCallback(() => {
    if (saved) {
      finalize();
      return;
    }
    if (savingRef.current) {
      pendingBackRef.current = true;
      return;
    }
    handleClose();
  }, [saved, finalize, handleClose]);
  useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

  // Auto-close the COMPOSE confirmation after a readable beat. Tap / Escape /
  // backdrop close it sooner so a fast user is never blocked. (Thread mode
  // never sets `saved`, so this never fires there.)
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(finalize, 2600);
    return () => clearTimeout(timer);
  }, [saved, finalize]);

  // Shared save: builds a BibleBookmark from `anchor` + the composed note.
  // COMPOSE uses the selection passage anchor; THREAD uses the single tapped
  // verse's anchor (so each add is a new {verse} note). The post-save split
  // is the only difference: COMPOSE flashes "Saved" then closes; THREAD
  // clears the composer and stays open (the new note appears in the list).
  const handleSave = useCallback(async () => {
    if (readOnly || !allowCreate) return;
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMutationError(null);
    // AB (rule #78 §4a): durable-write success, set the instant the bookmark is
    // persisted, so a deferred browser-back completes correctly on settle.
    let saveSucceeded = false;
    try {
      // Stop first so a final Enhanced transcription routes into the note.
      const interimAtStart = interim;
      // Merging interim at save time is also a yield (mirrors toggleMic), so
      // a save-with-interim is never mistaken for a failed recording.
      if (interimAtStart) gotFinalRef.current = true;
      if (isListening) await voice.stop();
      const latestText = textRef.current;
      const merged = interimAtStart
        ? latestText
          ? latestText + ' ' + interimAtStart
          : interimAtStart
        : latestText;
      // Note is OPTIONAL: '' is a valid bookmark (the verse selection is the
      // bookmark). No rescue / claim branch — bookmark audio never becomes a
      // Prayer.
      const note = merged.trim();
      const now = new Date().toISOString();
      const bookmark: BibleBookmark = {
        id: crypto.randomUUID(),
        bookSlug: anchor.bookSlug,
        chapter: anchor.chapter,
        anchor,
        note,
        createdAt: now,
        updatedAt: now,
      };
      // Keep model fill-empty (verse-tap redesign §2.1): when the verse's only
      // existing note is a single empty-note "Keep" row, the first thought
      // edits that row rather than creating a duplicate.
      // The reader can open this thread while its defensive IDB hydration is
      // still in flight. Re-read durably at commit time before deciding
      // whether to fill an existing empty Keep; otherwise a slow first load
      // could create a duplicate beside the unseen row.
      let commitNotes = notes;
      if (mode === 'thread') {
        await useBookmarkStore.getState().loadBookmarks();
        commitNotes = notesCoveringVerse(
          useBookmarkStore.getState().bookmarks,
          anchor.bookSlug,
          anchor.chapter,
          threadVerse,
        );
      }
      const soleEmptyKeep =
        mode === 'thread' &&
        commitNotes.length === 1 &&
        commitNotes[0].note.trim() === '';
      const inlineAudioClaims = await collectInlineAudioCommitClaims(
        committedInlineAudioIdsRef.current,
        'bookmark',
        note,
      );
      if (soleEmptyKeep) {
        await updateBookmark(
          commitNotes[0].id,
          note,
          commitNotes[0].updatedAt,
          inlineAudioClaims,
        );
      } else {
        await addBookmark(bookmark, inlineAudioClaims);
      }
      saveSucceeded = true;

      // Orphan cleanup is best-effort and happens only AFTER the durable write.
      // A cleanup failure must not turn a committed note into a visible failure
      // (which would invite a duplicate retry).
      if (committedInlineAudioIdsRef.current.size > 0) {
        if (inlineAudioClaims.length > 0) {
          try {
            await syncContentLifecycle();
            for (const claim of inlineAudioClaims) {
              await finalizeCommittedAudio(claim.queueItemId).catch(() => false);
            }
          } catch (err) {
            console.warn('[voice-capture] bookmark audio reconciliation deferred', err);
          }
        }
        committedInlineAudioIdsRef.current.clear();
        setAdoptedVoiceRecoveryId(null);
      }
      if (!mountedRef.current) return;
      if (mode === 'thread') {
        setText('');
        setVoiceFailed(false);
        setThreadPage(0);
        gotFinalRef.current = false;
      } else {
        setSaved(true);
      }
    } catch (err) {
      console.error('[Adia] bookmark save failed', err);
      if (mountedRef.current) setMutationError(saveRefusedForUnreadableRecording(err) ? 'saveReopen' : 'save');
    } finally {
      savingRef.current = false;
      if (mountedRef.current) setSaving(false);
      // AB (rule #78 §4a): complete a deferred browser/swipe back on save
      // settle. COMPOSE durable success -> finalize (onSaved clears the parent
      // selection); THREAD closes. On rejection the sheet remains open with
      // its draft and retry notice even though the history entry was consumed.
      if (pendingBackRef.current) {
        pendingBackRef.current = false;
        if (saveSucceeded) {
          if (onSaved) finalize();
          else handleClose();
        }
      }
    }
  }, [interim, isListening, voice, anchor, addBookmark, updateBookmark, notes, mode, threadVerse, setText, onSaved, finalize, handleClose, readOnly, allowCreate]);

  // Per-note edit save (text-only, D9).
  const handleEditSave = useCallback(
    async (id: string) => {
      if (readOnly) return;
      setEditingSaveId(id);
      setMutationError(null);
      try {
        if (!editingBaseUpdatedAt) throw new Error('bookmark-edit-base-missing');
        await updateBookmark(id, editText.trim(), editingBaseUpdatedAt);
        if (mountedRef.current) {
          setEditingId(null);
          setEditingBaseUpdatedAt(null);
          setStaleEdit(null);
        }
      } catch (err) {
        console.error('[Adia] bookmark edit failed', err);
        if (err instanceof BookmarkStaleEditError) {
          try {
            await useBookmarkStore.getState().loadBookmarks();
            const latest = useBookmarkStore
              .getState()
              .bookmarks.find((bookmark) => bookmark.id === id);
            if (mountedRef.current && latest) {
              setStaleEdit({
                id,
                latestText: latest.note,
                latestUpdatedAt: latest.updatedAt,
              });
              return;
            }
          } catch (loadErr) {
            console.warn('[Adia] bookmark conflict refresh failed', loadErr);
          }
        }
        if (mountedRef.current) setMutationError('edit');
      } finally {
        if (mountedRef.current) setEditingSaveId(null);
      }
    },
    [updateBookmark, editText, editingBaseUpdatedAt, readOnly],
  );

  // Per-note delete (in-place confirm). Deleting the LAST note covering this
  // verse closes the sheet; the reader's tappable cue disappears reactively.
  const handleDelete = useCallback(
    async (id: string) => {
      if (readOnly) return;
      const wasLast = notes.length <= 1;
      setDeletingId(id);
      setMutationError(null);
      try {
        await removeBookmark(id);
        if (!mountedRef.current) return;
        setConfirmingDeleteId(null);
        if (wasLast) onClose();
      } catch (err) {
        console.error('[Adia] bookmark delete failed', err);
        if (mountedRef.current) setMutationError('delete');
      } finally {
        if (mountedRef.current) setDeletingId(null);
      }
    },
    [notes.length, removeBookmark, onClose, readOnly],
  );

  // Verse-tap redesign §2.1: a freshly-kept verse is a single empty-note row.
  // In that state the thread hides the empty note row (+ its Edit/Remove) and
  // shows only the kept stamp + a "Stop keeping" action. Once a thought exists
  // (or a second covering row is added) the normal thread list renders.
  const keptNoThought =
    mode === 'thread' && notes.length === 1 && notes[0].note.trim() === '';
  // Per-row delete LABEL is scope-truthful: an empty row un-keeps the verse /
  // passage (no thought to delete), so it reuses the stop-keeping copy; a row
  // with a thought reads "Delete thought" (bookmark.removeKept).
  const rowDeleteLabel = (n: (typeof notes)[number]) =>
    n.note.trim() === ''
      ? n.anchor.verses.length > 1
        ? t('bookmark.stopKeepingPassage')
        : t('bookmark.stopKeeping')
      : t('bookmark.removeKept');

  const sheetAriaLabel =
    mode === 'compose' ? t('bookmark.sheetTitle') : anchor.reference;

  // 'page' mode (reflection route, §5): saved is never true (page is always
  // mode='thread'), the header is suppressed (the route owns it), and there is
  // no SheetShell / Escape / backdrop. 'sheet' mode is the shipped reader sheet.
  const inner = saved ? (
        <div
          className={styles.confirm}
          role="status"
          aria-live="polite"
          onClick={finalize}
        >
          <span className={styles.confirmMark} aria-hidden="true">
            ✦
          </span>
          <span className={styles.confirmText}>{t('bookmark.saved')}</span>
          <span className={styles.confirmRef}>{anchor.reference}</span>
        </div>
      ) : (
        <>
          {presentation === 'sheet' && (
            <div className={styles.header}>
              {mode === 'compose' && (
                <span className={styles.intro}>{t('bookmark.intro')}</span>
              )}
              <span className={styles.reference}>{anchor.reference}</span>
              <p className={styles.versePreview}>{anchor.text}</p>
            </div>
          )}
          <div className={styles.body}>
            {mutationError && (
              <p className={styles.mutationError} role="alert">
                {t(mutationError === 'saveReopen' ? 'voice.saveNeedsReopen' : `bookmark.${mutationError}Failed`)}
              </p>
            )}
            {/* Existing journal entries remain readable during the cutover,
                but no composer or microphone mounts. This is a UI gate on top
                of the bookmark-store persistence guard. */}
            {!readOnly && allowCreate && (
              <>
                <textarea
                  className={styles.textarea}
                  value={displayValue}
                  onChange={handleChange}
                  placeholder={t(
                    mode === 'thread'
                      ? 'bookmark.addThoughtPlaceholder'
                      : 'bookmark.placeholder',
                  )}
                  aria-label={t(
                    mode === 'thread'
                      ? 'bookmark.addThoughtPlaceholder'
                      : 'bookmark.placeholder',
                  )}
                  rows={4}
                />
                {voice.isSupported && (
                  <div className={styles.micArea}>
                    <MicButton
                      size="lg"
                      isListening={isListening}
                      isTranscribing={isTranscribing}
                      showLabel
                      onClick={toggleMic}
                      ariaLabel={
                        isListening
                          ? t('detail.stopRecording')
                          : t('detail.voiceInput')
                      }
                    />
                  </div>
                )}
              </>
            )}
            {/* THREAD note list: newest-first, below the composer. Each note
                carries its immutable created stamp (§6), a passage label when
                it covers more than this verse, the body (or an empty-note
                placeholder), and text-only edit + delete. */}
            {/* Kept, no thought yet (verse-tap redesign §2.1): a single
                empty-note Keep row. Show the kept stamp + a no-confirm
                "Stop keeping" action (scope-truthful for a multi-verse
                passage); the empty note row + its Edit/Remove are hidden until
                a thought exists. */}
            {/* D4 bookmark notice (2026-10-18): a bound, commit-pending row whose
                destination is THIS entry and whose receipt the server lacks
                shows its words read-only, inside the entry's own row (both the
                kept-no-thought standalone and every thread row below), in
                read-only mode too. It lives in the body, never the footer the
                sheet hides while recording or read-only, and is suppressed
                while this sheet's own save is in flight. Compose shows a bare
                anchor and no entry, so it mounts nothing there. */}
            {keptNoThought && (
              <div className={styles.keptStandalone}>
                <div className={styles.noteRow}>
                  <div className={styles.noteStamp}>
                    {formatFullDateTime(notes[0].createdAt, i18n.language)}
                  </div>
                  <BookmarkUnconfirmedSaveNotice bookmark={notes[0]} suppressed={saving} />
                  {!readOnly && <div className={styles.noteActions}>
                    <button
                      type="button"
                      className={styles.delete}
                      disabled={deletingId === notes[0].id}
                      onClick={() => handleDelete(notes[0].id)}
                    >
                      {deletingId === notes[0].id
                        ? t('bookmark.deleting')
                        : notes[0].anchor.verses.length > 1
                          ? t('bookmark.stopKeepingPassage')
                          : t('bookmark.stopKeeping')}
                    </button>
                  </div>}
                </div>
              </div>
            )}
            {!keptNoThought && mode === 'thread' && notes.length > 0 && (
              <ul className={styles.thread}>
                {shownNotes.map((n) => (
                  <li key={n.id} className={styles.noteRow}>
                    <div className={styles.noteStamp}>
                      {formatFullDateTime(n.createdAt, i18n.language)}
                    </div>
                    {n.anchor.verses.length > 1 && (
                      <div className={styles.noteLabel}>
                        {formatVerseRanges(n.anchor.verses)}
                      </div>
                    )}
                    <BookmarkUnconfirmedSaveNotice bookmark={n} suppressed={saving} />
                    {!readOnly && editingId === n.id ? (
                      <>
                        <textarea
                          className={styles.editTextarea}
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          aria-label={t('bookmark.addThoughtPlaceholder')}
                          rows={3}
                        />
                        {staleEdit?.id === n.id && (
                          <div className={styles.editConflict} role="alert">
                            <p>{t('bookmark.editConflict')}</p>
                            <div className={styles.editConflictLatest}>
                              <span>{t('bookmark.editConflictLatest')}</span>
                              <p>{staleEdit.latestText || t('bookmark.emptyNotePlaceholder')}</p>
                            </div>
                            <div className={styles.noteActions}>
                              <button
                                type="button"
                                className={styles.noteAction}
                                onClick={() => {
                                  setEditText(staleEdit.latestText);
                                  setEditingBaseUpdatedAt(staleEdit.latestUpdatedAt);
                                  setStaleEdit(null);
                                }}
                              >
                                {t('bookmark.editConflictUseLatest')}
                              </button>
                              <button
                                type="button"
                                className={styles.noteActionPrimary}
                                onClick={() => {
                                  setEditingBaseUpdatedAt(staleEdit.latestUpdatedAt);
                                  setStaleEdit(null);
                                }}
                              >
                                {t('bookmark.editConflictKeepMine')}
                              </button>
                            </div>
                          </div>
                        )}
                        {!readOnly && <div className={styles.noteActions}>
                          <button
                            type="button"
                            className={styles.noteAction}
                            disabled={editingSaveId === n.id}
                            onClick={() => {
                              setEditingId(null);
                              setEditingBaseUpdatedAt(null);
                              setStaleEdit(null);
                            }}
                          >
                            {t('bookmark.cancel')}
                          </button>
                          <button
                            type="button"
                            className={styles.noteActionPrimary}
                            disabled={editingSaveId === n.id}
                            onClick={() => handleEditSave(n.id)}
                          >
                            {t(
                              editingSaveId === n.id
                                ? 'bookmark.saving'
                                : 'bookmark.editSave',
                            )}
                          </button>
                        </div>}
                      </>
                    ) : (
                      <>
                        {n.note ? (
                          <p className={styles.noteBody}>{n.note}</p>
                        ) : (
                          <p className={styles.emptyNote}>
                            {t('bookmark.emptyNotePlaceholder')}
                          </p>
                        )}
                        {!readOnly && <div className={styles.noteActions}>
                          {confirmingDeleteId === n.id ? (
                            <>
                              <span className={styles.deleteConfirmText}>
                                {t('bookmark.deleteConfirm')}
                              </span>
                              <button
                                type="button"
                                className={styles.noteAction}
                                disabled={deletingId === n.id}
                                onClick={() => setConfirmingDeleteId(null)}
                              >
                                {t('bookmark.cancel')}
                              </button>
                              <button
                                type="button"
                                className={styles.delete}
                                disabled={deletingId === n.id}
                                onClick={() => handleDelete(n.id)}
                              >
                                {t(
                                  deletingId === n.id
                                    ? 'bookmark.deleting'
                                    : 'bookmark.deleteConfirmAction',
                                )}
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className={styles.noteAction}
                                onClick={() => {
                                  setConfirmingDeleteId(null);
                                  setEditingId(n.id);
                                  setEditText(n.note);
                                  setEditingBaseUpdatedAt(n.updatedAt);
                                  setStaleEdit(null);
                                }}
                              >
                                {t('bookmark.edit')}
                              </button>
                              <button
                                type="button"
                                className={styles.delete}
                                disabled={deletingId === n.id}
                                onClick={() => {
                                  // Empty row: nothing written to lose, so
                                  // un-keep directly (no confirm) — parity with
                                  // the keptNoThought action. A row WITH a
                                  // thought keeps the in-place confirm.
                                  if (n.note.trim() === '') {
                                    handleDelete(n.id);
                                  } else {
                                    setEditingId(null);
                                    setEditingBaseUpdatedAt(null);
                                    setConfirmingDeleteId(n.id);
                                  }
                                }}
                              >
                                {deletingId === n.id
                                  ? t('bookmark.deleting')
                                  : rowDeleteLabel(n)}
                              </button>
                            </>
                          )}
                        </div>}
                      </>
                    )}
                  </li>
                ))}
              </ul>
            )}
            {!keptNoThought && mode === 'thread' && notes.length > 0 && (
              <JournalPagination
                page={boundedThreadPage}
                totalItems={notes.length}
                onPageChange={setThreadPage}
              />
            )}
          </div>
          {/* Pinned below the scrolling body so Cancel/Save stay on-screen
              on a phone when the verse preview + mic stack past the sheet
              max-height. Hidden while recording/transcribing (rule #22).
              Save is NOT disabled on empty text: an empty note is a valid
              verse-only bookmark. */}
          {!readOnly && allowCreate && !isListening && !isTranscribing && (
            <div className={styles.footer} data-keyboard-actions="">
              {(voiceFailed || bookmarkRecoveryCount > 0) && (
                <div className={styles.voiceRecovery}>
                  {discardVoiceFailed && bookmarkRecoveryCount > 0 && (
                    <p className={styles.voiceFailed} role="alert">
                      {t('voice.discardFailed')}
                    </p>
                  )}
                  <p className={styles.voiceFailed} role="status">
                    {bookmarkRecoveryParked
                      ? t('bookmark.voiceRecoveryParked')
                      : bookmarkRecoveryText
                        ? t('bookmark.voiceRecoveryTextReady')
                        : bookmarkAudioRecoveryCount > 0 && !enhancedVoiceAllowed()
                          ? t('bookmark.voiceRecoveryParked')
                        : bookmarkRecoveryStatus === 'processing'
                          ? t('bookmark.voiceRecoveryPreparing')
                        : bookmarkRecoveryStatus === 'check-later'
                          ? t('bookmark.voiceRecoveryCheckLater')
                        : bookmarkRecoveryStatus === 'failed'
                          ? t('bookmark.voiceRecoveryNotPrepared')
                        : bookmarkRecoveryCount > 0
                          ? t('bookmark.voiceRecoveryStored')
                      : t('bookmark.voiceFailed')}
                  </p>
                  {bookmarkRecoveryCount > 0 && (
                    <div className={styles.voiceRecoveryActions}>
                      {canPrepareBookmarkWords && (
                        <button
                          type="button"
                          className={styles.useRecovery}
                          onClick={handlePrepareBookmarkWords}
                        >
                          {t('bookmark.prepareVoiceRecovery')}
                        </button>
                      )}
                      {bookmarkRecoveryText &&
                        !bookmarkRecoveryParked &&
                        adoptedVoiceRecoveryId !== bookmarkRecovery?.id && (
                        <button
                          type="button"
                          className={styles.useRecovery}
                          onClick={handleUseVoiceRecovery}
                        >
                          {t('bookmark.useVoiceRecovery')}
                        </button>
                      )}
                      {!bookmarkRecoveryParked &&
                        adoptedVoiceRecoveryId !== bookmarkRecovery?.id && (
                        <button
                          type="button"
                          className={styles.discardAudio}
                          disabled={discardingVoiceRecovery}
                          onClick={() => { void handleDiscardVoiceRecovery(); }}
                        >
                          {t(
                            bookmarkRecoveryText
                              ? 'bookmark.discardRecoveredWords'
                              : 'bookmark.discardVoiceRecovery',
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              {showSaveCue && (
                <div className={styles.hint} aria-hidden="true">
                  {t('bookmark.hint')}
                </div>
              )}
              <div className={styles.actions}>
                <div className={styles.spacer} />
                {mode === 'compose' && (
                  <button
                    type="button"
                    className={styles.cancel}
                    onClick={handleClose}
                  >
                    {t('bookmark.cancel')}
                  </button>
                )}
                <button
                  type="button"
                  className={`${styles.save} ${
                    showSaveCue ? styles.saveCue : ''
                  }`.trim()}
                  disabled={saving}
                  onClick={handleSave}
                >
                  {saving
                    ? t('bookmark.saving')
                    : mode === 'compose'
                      ? t('bookmark.save')
                      : t('bookmark.addThought')}
                </button>
              </div>
            </div>
          )}
        </>
      );

  if (presentation === 'page') {
    return <div className={styles.pageBody}>{inner}</div>;
  }

  return (
    <SheetShell
      ariaLabel={sheetAriaLabel}
      onBackdrop={handleBackdrop}
      closeLabel={t('summary.close')}
      zIndex={zIndex}
      dragToDismiss
    >
      <div className={styles.sheetInner}>{inner}</div>
    </SheetShell>
  );
});

export default BibleBookmarkSheet;
